import {
  Drawer,
  Input,
  Select,
  Button,
  Collapse,
  message,
  Modal,
  DatePicker,
  TimePicker,
  Grid,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  AimOutlined,
  TagsOutlined,
  FlagOutlined,
  FileTextOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addLeadPurpose,
  addLeadSource,
  addLeadStatus,
  checkLeadPhone,
  createLead,
  fetchLeadPurpose,
  fetchLeads,
  fetchLeadSource,
  fetchLeadStatus,
  updateLead,
} from "../leadThunk";
import { getCountryList } from "../../../../lib/currency";
import parsePhoneNumberFromString, { getCountryCallingCode } from "libphonenumber-js";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import { fetchCrmFieldsApi } from "../../../../apiservice/crm";
import { formatToUtc, isAdmin } from "../../../../lib/functions";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import "../../crmSharedStyle.css";

const { Panel } = Collapse;
const { useBreakpoint } = Grid;

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div className="crm-section-title">{title}</div>
    {children}
  </div>
);

const FieldLabel = ({ icon, children }) => (
  <div className="crm-field-label">
    {icon}
    {children}
  </div>
);

const LeadDrawer = ({ open, mode, lead, onClose, defaultStatus, users, defaultCountry }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { createLoading, statusList, sourceList, purposeList } = useSelector(
    (s) => s.leads
  );
  const authInfo = useSelector(selectAuthInfo);
  const adminUser = isAdmin(authInfo);

  console.log(adminUser)

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    address: "",
    assigned_to: null,
    source: null,
    status: null,
    purpose:null,
    profession: "",
    country: "",
    notes: "",
    follow_up_title: "",
  });

  const [followDate, setFollowDate] = useState(null);
  const [followTime, setFollowTime] = useState(null);

  // Normally locked to the company's own country. A pasted number that
  // carries its own country code (see handlePhonePaste) switches this --
  // that's the one case the user wants the lock overridden entirely.
  const [phoneCountry, setPhoneCountry] = useState(defaultCountry);

  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  const [sourceModal, setSourceModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [purposeModal, setPurposeModal] = useState(false);
  const [newSource, setNewSource] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusColor, setStatusColor] = useState("blue");
  const [newPurpose, setNewPurpose] = useState("");
  const [phoneDuplicate, setPhoneDuplicate] = useState(null);

  const countryOptions = getCountryList();

  const currentUserId = authInfo?.userInfo?.user_id;
  const currentUserName = authInfo?.userInfo?.full_name;

  const update = (key, value) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleCustomChange = (fieldId, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // 🔥 fetch sources + status
  useEffect(() => {
      dispatch(fetchLeadSource());
      dispatch(fetchLeadStatus());
      dispatch(fetchLeadPurpose());
    }, []);

    useEffect(() => {
    if (defaultStatus) {
      setNewStatus(defaultStatus);
    }
  }, [defaultStatus]);

  // Fresh drawer session starts locked to the company's own country again,
  // so a detected-country paste on one lead doesn't linger into the next.
  useEffect(() => {
    if (open) {
      setPhoneCountry(defaultCountry);
    }
  }, [open, defaultCountry]);

  useEffect(() => {
    if (mode === "edit" && lead) {
      setForm({
        name: lead.name || "",
        phone: lead.phone || "",
        email: lead.email || "",
        company: lead.company || "",
        address: lead.address || "",
        assigned_to: lead.assigned_to || null,
        source: lead.source_id || null,
        status: lead.status_id || null,
        purpose: lead.purpose_id || null,
        profession: lead.profession || "",
        country: lead.country || "",
        notes: lead.notes || "",
        follow_up_title: "",
      });

      const mappedCustomValues = {};

      customFields.forEach((field) => {
        const existingValue = lead.custom_fields?.[field.field_key];

        if (existingValue !== undefined) {
          mappedCustomValues[field.id] = existingValue;
        }
      });

      setCustomValues(mappedCustomValues);
    }
  }, [lead, mode, customFields]);


  // The "Add Lead" form is kept as a draft across closes (e.g. clicking
  // outside the drawer) and is only cleared after a successful create.
  const resetForm = () => {
    setForm({
      name: "",
      phone: "",
      email: "",
      company: "",
      address: "",
      assigned_to: adminUser ? null : currentUserId,
      source: sourceList.find((s) => s.name === "Manual")?.id || null,
      status: statusList.find((s) => s.code === "new")?.id || null,
      purpose: null,
      profession: "",
      country: "",
      notes: "",
      follow_up_title: "",
    });
    setCustomValues({});
    setFollowDate(null);
    setFollowTime(null);
    setPhoneDuplicate(null);
  };

  // Edit mode shares the same form state, so stash the create draft when
  // switching to edit and bring it back when returning to create.
  const createDraftRef = useRef(null);
  const prevModeRef = useRef(mode);

  useEffect(() => {
    const prevMode = prevModeRef.current;
    prevModeRef.current = mode;

    if (prevMode === "create" && mode === "edit") {
      createDraftRef.current = {
        form,
        customValues,
        followDate,
        followTime,
        phoneDuplicate,
      };
    } else if (prevMode === "edit" && mode === "create") {
      const draft = createDraftRef.current;
      createDraftRef.current = null;
      if (draft) {
        setForm(draft.form);
        setCustomValues(draft.customValues);
        setFollowDate(draft.followDate);
        setFollowTime(draft.followTime);
        setPhoneDuplicate(draft.phoneDuplicate);
      } else {
        resetForm();
      }
    }
  }, [mode]);

  // Non-admins can only assign leads to themselves.
  useEffect(() => {
    if (mode === "create" && !adminUser && currentUserId && !form.assigned_to) {
      update("assigned_to", currentUserId);
    }
  }, [mode, adminUser, currentUserId, form.assigned_to]);

  // 🔥 fetch custom fields
  useEffect(() => {
    const loadFields = async () => {
      try {
        const res = await fetchCrmFieldsApi({ module: "lead" });
        
         setCustomFields(res.data || []);
      } catch (err) {
        console.log("custom field fetch error", err);
      }
    };
    loadFields();
  }, []);

  // defaults
  useEffect(() => {
    if (sourceList.length && !form.source) {
      const manual = sourceList.find((s) => s.name === "Manual");
      if (manual) update("source", manual.id);
    }
    if (statusList.length && !form.status) {
      const def = statusList.find((s) => s.code === "new");
      if (def) update("status", def.id);
    }
  }, [sourceList, statusList]);

  const submit = async () => {
    
    if (!form.name) return message.warning("Enter name");
    if (!form.status) return message.warning("Select a status");
    if (!form.phone) return message.warning("Enter phone");
    if (phoneDuplicate) {
      return message.error("A lead already exists with this phone number.");
    }

    const parsed = parsePhoneNumberFromString(`+${form.phone}`);
    if (!parsed || !parsed.isValid()) {
      return message.error("Invalid phone");
    }

    let combinedDateTime = null;
    if (followDate) {
      const localDateTime = dayjs(followDate)
        .hour(followTime ? dayjs(followTime).hour() : 0)
        .minute(followTime ? dayjs(followTime).minute() : 0)
        .second(0)
        .format("YYYY-MM-DD HH:mm:ss");

      combinedDateTime = formatToUtc(localDateTime);
    }

    const payload = {
      ...form,
      id: lead?.id,
      status: form.status,
      source: form.source,
      follow_up_date: combinedDateTime,
      custom_fields: Object.entries(customValues).map(
        ([field_id, value]) => ({
          field_id,
          value,
        })
      ),
    };

    try {
      if (mode === "edit") {
        await dispatch(updateLead(payload)).unwrap();

        message.success("Lead updated ✅");
      } else {
        const res = await dispatch(
          createLead(payload)
        ).unwrap();

        if (res?.duplicate) {
          message.info("Lead exists → opening 🔁");
          navigate(`/crm/leads/${res.id}`);
          return;
        }

        message.success("Lead created ✅");
        resetForm();
      }

      onClose();
      dispatch(fetchLeads());

    } catch {
      message.error("Failed ❌");
    }
  };

  const validatePhone = async (phone) => {
    if (!phone) return;

    try {
      const res = await dispatch(
        checkLeadPhone({
          phone,
          lead_id: mode === "edit" ? lead?.id : null,
        })
      ).unwrap();

      if (res.exists) {
        setPhoneDuplicate(res);

        message.warning(
          `Lead already exists: ${res.lead_name}`
        );
      } else {
        setPhoneDuplicate(null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Numbers copied from elsewhere (WhatsApp, Excel, another CRM...) almost
  // always carry their own "+91" or similar prefix, and/or human formatting
  // (spaces, dashes, brackets). react-phone-input-2's own native paste
  // merge is fragile against both: with the country code locked, a foreign
  // prefix just gets merged into the fixed-length mask for our locked
  // country (real digits pushed past the end and cut off), and formatting
  // characters in the middle of the pasted text can make it reject the
  // paste outright and blank the field instead of just ignoring them. So
  // every paste is handled here, never left to the library's native
  // handler -- we always strip formatting ourselves and set the digits
  // directly.
  //
  // If the paste carries its own recognisable country code, that's a
  // deliberate signal from the user -- copy/paste is the one way they can
  // hand us a *different* country's number on purpose, so the whole field
  // (country + number) switches to match it. A plain local-format paste
  // (no + or 00) has no such signal, so it stays under the currently
  // selected country exactly as before.
  // The ITU E.164 standard caps any real phone number at 15 total digits
  // (country code + subscriber number), so this leaves comfortable slack
  // for odd/extension-style numbers while still catching an accidental
  // paste of something that isn't a phone number at all (an ID, an
  // address...). react-phone-input-2's own `enableLongNumbers` cap never
  // applies here since we preventDefault() every paste ourselves below,
  // so it's enforced by hand.
  const MAX_PHONE_DIGITS = 20;
  const capDigits = (digits) => digits.slice(0, MAX_PHONE_DIGITS);

  const handlePhonePaste = (e) => {
    const pasted =
      (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const trimmed = pasted.trim();
    if (!trimmed) return;

    e.preventDefault();

    if (!/^(\+|00)\d/.test(trimmed)) {
      // Plain local-format paste -- keep it under the currently selected
      // country, just strip any spaces/dashes/brackets first.
      const nationalDigits = trimmed.replace(/\D/g, "");
      const lockedDialCode = getCountryCallingCode(
        (phoneCountry || defaultCountry || "ae").toUpperCase()
      );
      update("phone", capDigits(`${lockedDialCode}${nationalDigits}`));
      return;
    }

    const normalized = trimmed.startsWith("00")
      ? `+${trimmed.slice(2)}`
      : trimmed;

    const parsed = parsePhoneNumberFromString(normalized);

    if (parsed?.countryCallingCode) {
      // A calling code was identified -- hand the number over to it. The
      // country (ISO code) can stay undetermined for a shared/ambiguous
      // calling code (e.g. NANP "+1") on a non-standard length number;
      // only switch the selected country when one was actually pinned
      // down, but always use the real calling code either way so digits
      // never get recombined under the wrong prefix.
      if (parsed.country) {
        setPhoneCountry(parsed.country.toLowerCase());
      }
      update("phone", capDigits(`${parsed.countryCallingCode}${parsed.nationalNumber}`));
      return;
    }

    // Had a +/00 prefix but libphonenumber-js couldn't recognise any
    // calling code at all (e.g. the pasted text is too long to parse as
    // an international number at all) -- keep exactly the digits that
    // were pasted rather than inventing a dial code. Re-prepending the
    // locked country's dial code here would double it up whenever the
    // pasted prefix happens to match, since these digits already include
    // whatever prefix the user pasted.
    const nationalDigits = normalized.replace(/\D/g, "");

    update("phone", capDigits(nationalDigits));
  };




  return (
    <Drawer
      title={mode === "edit" ? "Edit Lead" : "Add Lead"}
      open={open}
      onClose={onClose}
      width={screens.md ? 420 : "100%"}
      className="crm-drawer"
      styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
    >
      {/* CUSTOMER */}
      <Section title="Customer Details">
        <FieldLabel icon={<UserOutlined />}>Customer name</FieldLabel>
        <Input
          placeholder="Customer Name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
        />

        <div style={{ marginTop: 12 }} className="crm-field-label">
          <TeamOutlined />
          Company
        </div>
        <Input
          placeholder="Company"
          value={form.company}
          onChange={(e) => update("company", e.target.value)}
        />

        <div style={{ marginTop: 12 }} className="crm-field-label">
          Phone
        </div>
        <PhoneInput
          // react-phone-input-2 doesn't reset its internal masked-input
          // state when `value` goes back to "" while staying mounted (the
          // drawer has no destroyOnClose) -- after a successful create
          // resets form.phone, the next time the drawer opens it shows a
          // stale country/format and won't accept typing. Keying it to the
          // open/close transition forces a clean remount each time. Also
          // keyed to phoneCountry: changing `country` and `value` in the
          // same update (a detected-country paste) doesn't apply cleanly
          // either without a remount -- the library drops the pasted
          // digits and shows only the new country's bare dial code.
          key={`${open ? "phone-input-open" : "phone-input-closed"}-${phoneCountry}`}
          country={phoneCountry}
          countryCodeEditable={false}
          // Default per-country mask caps the number at that country's
          // standard length (e.g. 10 digits for India) and silently
          // truncates anything longer, typed or pasted. Numbers longer
          // than that need to be allowed, so the mask is disabled -- but
          // typing is still capped at MAX_PHONE_DIGITS (pasting is capped
          // separately in handlePhonePaste, since it bypasses this prop
          // entirely) so the field can't grow without bound.
          enableLongNumbers={MAX_PHONE_DIGITS}
          value={form.phone}
          onChange={(v) => update("phone", v)}
          onBlur={() => validatePhone(form.phone)}
          inputStyle={{ width: "100%" }}
          inputProps={{ onPaste: handlePhonePaste }}
        />

        {phoneDuplicate && (
          <div
            style={{
              color: "#ff4d4f",
              fontSize: 12,
              marginTop: 5,
            }}
          >
            Lead already exists: {phoneDuplicate.lead_name}
          </div>
        )}

        <div style={{ marginTop: 12 }} className="crm-field-label">
          Email
        </div>
        <Input
          placeholder="Email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
        />

        <div style={{ marginTop: 12 }} className="crm-field-label">
          Address
        </div>
        <Input
          placeholder="Address"
          value={form.address}
          onChange={(e) => update("address", e.target.value)}
        />

        <div style={{ marginTop: 12 }}>
          <FieldLabel icon={<TeamOutlined />}>Agent</FieldLabel>
          <Select
            placeholder="Assign Agent"
            value={form.assigned_to}
            onChange={(v) => update("assigned_to", v)}
            disabled={!adminUser}
            options={
              adminUser
                ? (users || []).map((u) => ({
                    label: u.full_name,
                    value: u.id,
                  }))
                : [
                    {
                      label: currentUserName,
                      value: currentUserId,
                    },
                  ]
            }
            style={{ width: "100%" }}
          />
        </div>
      </Section>

      {/* LEAD INFO */}
      <Section title="Lead Info">
        <div className="crm-form-section">
          <FieldLabel icon={<AimOutlined />}>Source</FieldLabel>
          <Select
            style={{ width: "100%" }}
            placeholder="Source"
            value={form.source}
            onChange={(v) => update("source", v)}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: 8 }}>
                  <Button type="link" onClick={() => setSourceModal(true)}>
                    + Add Source
                  </Button>
                </div>
              </>
            )}
            options={sourceList.map((s) => ({
              label: s.name,
              value: s.id,
            }))}
          />
        </div>

        <div className="crm-form-section">
          <FieldLabel icon={<TagsOutlined />}>Status</FieldLabel>
          <Select
            style={{ width: "100%" }}
            placeholder="Status"
            value={form.status}
            onChange={(v) => update("status", v)}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: 8 }}>
                  <Button type="link" onClick={() => setStatusModal(true)}>
                    + Add Status
                  </Button>
                </div>
              </>
            )}
            options={statusList.map((s) => ({
              label: s.name,
              value: s.id,
            }))}
          />
        </div>

        <div className="crm-form-section" style={{ marginBottom: 0 }}>
          <FieldLabel icon={<FlagOutlined />}>Purpose</FieldLabel>
          <Select
            placeholder="Purpose"
            style={{ width: "100%" }}
            value={form.purpose}
            onChange={(v) => update("purpose", v)}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: 8 }}>
                  <Button type="link" onClick={() => setPurposeModal(true)}>
                    + Add Purpose
                  </Button>
                </div>
              </>
            )}
            options={purposeList.map((s) => ({
              label: s.name,
              value: s.id,
            }))}
          />
        </div>
      </Section>

      {/* NOTES */}
      {mode === "create" && (
        <Section title="Notes">
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 10 }}
            placeholder="Customer feedback"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
          />
        </Section>
      )}

      {/* FOLLOWUP */}
      {mode === "create" && (
        <Section title="Follow-up">
          <FieldLabel icon={<FileTextOutlined />}>Title</FieldLabel>
          <Input
            placeholder="Title"
            value={form.follow_up_title}
            onChange={(e) => update("follow_up_title", e.target.value)}
          />

          <div className="crm-form-row" style={{ marginTop: 12 }}>
            <div>
              <FieldLabel icon={<CalendarOutlined />}>Date</FieldLabel>
              <DatePicker
                style={{ width: "100%" }}
                value={followDate}
                onChange={(d) => setFollowDate(d)}
              />
            </div>
            <div>
              <FieldLabel icon={<CalendarOutlined />}>Time</FieldLabel>
              <TimePicker
                style={{ width: "100%" }}
                value={followTime}
                onChange={(t) => setFollowTime(t)}
              />
            </div>
          </div>
        </Section>
      )}

      {/* CUSTOM FIELDS */}
      <Section title="Additional Fields">
        {customFields
          .filter((f) => f.show_in_form)
          .map((f) => {
            return (
              <div key={f.id} style={{ marginTop: 12 }}>
                {/* LABEL */}
                <div className="crm-field-label" style={{ textTransform: "none" }}>
                  {f.field_name} {f.is_required && "*"}
                </div>

                {/* TEXT */}
                {f.type === "text" && (
                  <Input
                    placeholder={f.field_name}
                     value={customValues[f.id] || ""}
                    onChange={(e) => handleCustomChange(f.id, e.target.value)}
                  />
                )}

                {/* NUMBER */}
                {f.type === "number" && (
                  <Input
                    type="number"
                    placeholder={f.field_name}
                    value={customValues[f.id] ?? ""}
                    onChange={(e) => handleCustomChange(f.id, e.target.value)}
                  />
                )}

                {/* DROPDOWN */}
                {f.type === "dropdown" && (
                  <Select
                    placeholder={f.field_name}
                    options={(f.options || []).map((o) => ({
                      label: o, // ✅ FIXED
                      value: o,
                    }))}
                    value={customValues[f.id] ?? undefined}
                    onChange={(v) => handleCustomChange(f.id, v)}
                  />
                )}

                {/* MULTI SELECT */}
                {f.type === "multi_select" && (
                  <Select
                    mode="multiple"
                    placeholder={f.field_name}
                    style={{ width: "100%" }}
                    value={
                      Array.isArray(customValues[f.id])
                        ? customValues[f.id]
                        : customValues[f.id]
                        ? (() => {
                            try {
                              return JSON.parse(customValues[f.id]);
                            } catch {
                              return [];
                            }
                          })()
                        : []
                    }
                    options={(f.options || []).map((o) => ({
                      label: o,
                      value: o,
                    }))}
                    onChange={(v) =>
                      handleCustomChange(f.id, JSON.stringify(v))
                    }
                  />
                )}

                {/* DATE */}
                {f.type === "date" && (
                  <DatePicker
                    style={{ width: "100%" }}
                    value={customValues[f.id] ? dayjs(customValues[f.id]) : null}
                    onChange={(d) =>
                      handleCustomChange(
                        f.id,
                        d ? d.format("YYYY-MM-DD") : null,
                      )
                    }
                  />
                )}

                {/* DATETIME */}
                {f.type === "datetime" && (
                  <DatePicker
                    showTime
                    style={{ width: "100%" }}
                    value={customValues[f.id] ? dayjs(customValues[f.id]) : null}
                    onChange={(d) =>
                      handleCustomChange(
                        f.id,
                        d ? d.format("YYYY-MM-DD HH:mm:ss") : null,
                      )
                    }
                  />
                )}
              </div>
            );
          })}
      </Section>

      <div className="crm-drawer-footer">
        <Button type="primary" size="large" block loading={createLoading} onClick={submit}>
          {mode === "edit" ? "Update Lead" : "Create Lead"}
        </Button>
      </div>

      <Modal
        title="Add New Source"
        open={sourceModal}
        onCancel={() => setSourceModal(false)}
        className="crm-modal-responsive"
        onOk={async () => {
          if (!newSource.trim()) return message.warning("Enter source name");

          try {
            const res = await dispatch(
              addLeadSource({ name: newSource }),
            ).unwrap();
            update("source", res.id);
            await dispatch(fetchLeadSource()); // refresh list
            message.success("Source added ✅");

            setSourceModal(false);
            setNewSource("");

          } catch(err) {
            const errMsg =
                typeof err === "string"
                  ? err
                  : err?.message || err?.msg || "Failed ❌";

              message.error(
                errMsg.toLowerCase().includes("exist")
                  ? "Source already exists"
                  : errMsg
              );
          }
        }}
      >
        <Input
          placeholder="Enter source name"
          value={newSource}
          onChange={(e) => setNewSource(e.target.value)}
        />
      </Modal>

      <Modal
        title="Add New Status"
        open={statusModal}
        onCancel={() => setStatusModal(false)}
        className="crm-modal-responsive"
        onOk={async () => {
          if (!newStatus.trim()) return message.warning("Enter status name");

          try {
            const res = await dispatch(
              addLeadStatus({
                name: newStatus,
                color: statusColor,
              }),
            ).unwrap();

            update("status", res.id);
            await dispatch(fetchLeadStatus());

            message.success("Status added ✅");

            setStatusModal(false);
            setNewStatus("");
            setStatusColor("blue");

           } catch (err) {
              const errMsg =
                typeof err === "string"
                  ? err
                  : err?.message || err?.msg || "Failed ❌";

              message.error(
                errMsg.toLowerCase().includes("exist")
                  ? "Status already exists"
                  : errMsg
              );
            }
        }}
      >
        <Input
          placeholder="Status name"
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          style={{ marginBottom: 10 }}
        />

        <Select
          value={statusColor}
          onChange={setStatusColor}
          style={{ width: "100%" }}
          options={[
            { label: "Blue", value: "blue" },
            { label: "Green", value: "green" },
            { label: "Red", value: "red" },
            { label: "Orange", value: "orange" },
            { label: "Purple", value: "purple" },
          ]}
        />
      </Modal>

      <Modal
        title="Add Purpose"
        open={purposeModal}
        onCancel={() => setPurposeModal(false)}
        className="crm-modal-responsive"
        onOk={async () => {
          if (!newPurpose.trim()) {
            return message.warning("Enter purpose name");
          }

          try {
            await dispatch(addLeadPurpose({ name: newPurpose })).unwrap();

            message.success("Purpose added ✅");

            setPurposeModal(false);
            setNewPurpose("");

            dispatch(fetchLeadPurpose());
          } catch {
            message.error("Failed ❌");
          }
        }}
      >
        <Input
          placeholder="Purpose name"
          value={newPurpose}
          onChange={(e) => setNewPurpose(e.target.value)}
        />
      </Modal>
    </Drawer>
  );
};

export default LeadDrawer;
